/**
 * 手机端 WebSocket 客户端：连接中继服务器、认证、发 cmd、收 stream/result，自动重连。
 * 与 shared 协议一致（见 shared/src/types.ts）。
 */
import { makeNonce, makeProof, type DeviceStatus, type FsEvent, type ProbeData, type ScreenFrame, type ServerToPhoneMsg } from '@ai-mobile/shared';

export type ConnState = 'idle' | 'connecting' | 'open' | 'closed';

type ResultMsg = Extract<ServerToPhoneMsg, { type: 'cmd:result' }>;

interface Pending {
  resolve: (r: ResultMsg) => void;
  reject: (e: Error) => void;
  chunks: string[];
  timer: ReturnType<typeof setTimeout>;
}

export interface CmdOutcome {
  result: ResultMsg;
  chunks: string[];
}

/** 把用户输入的服务器地址规范化为 WebSocket URL（http→ws / https→wss） */
export function normalizeWsUrl(raw: string): string {
  const s = raw.trim();
  if (!s) throw new Error('服务器地址不能为空');
  if (/^wss?:\/\//i.test(s)) return s;
  if (/^https:\/\//i.test(s)) return s.replace(/^https/i, 'wss');
  if (/^http:\/\//i.test(s)) return s.replace(/^http/i, 'ws');
  return `wss://${s}`;
}

export class WsClient {
  private ws: WebSocket | null = null;
  private token = '';
  private url = '';
  private pending = new Map<string, Pending>();
  private nextId = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 800;
  private manualClose = false;
  /** 本次连接认证用的 nonce（用于校验服务器 proof） */
  private nonce = '';

  onConnState?: (s: ConnState) => void;
  onAuthOk?: (devices: DeviceStatus[]) => void;
  onAuthError?: (reason: string) => void;
  onStatus?: (devices: DeviceStatus[]) => void;
  /** 收到屏幕回显帧（base64 JPEG，订阅设备产生） */
  onScreenFrame?: (f: ScreenFrame) => void;
  /** 收到文件变更事件（fs.watch 推送；path 为被监视目录，files 为变更文件 basename） */
  onFsEvent?: (e: FsEvent) => void;
  /** 收到探针采样帧（probe.watch 推送；fast 每帧必带，slow/ctrl 首采完成前缺省） */
  onProbeData?: (d: ProbeData) => void;
  onError?: (reason: string) => void;

  get isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  connect(url: string, token: string): void {
    this.url = url;
    this.token = token;
    this.manualClose = false;
    this.reconnectDelay = 800;
    this.open();
  }

  disconnect(): void {
    this.manualClose = true;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.rejectAll(new Error('连接已断开'));
    try {
      this.ws?.close();
    } catch {
      /* noop */
    }
    this.ws = null;
    this.onConnState?.('idle');
  }

  send(obj: unknown): void {
    if (!this.isOpen) throw new Error('未连接');
    this.ws!.send(JSON.stringify(obj));
  }

  subscribe(deviceId: string): void {
    try {
      this.send({ type: 'subscribe', deviceId });
    } catch {
      /* noop */
    }
  }

  cmd(deviceId: string, action: string, params: Record<string, unknown> = {}, timeoutMs = 120_000): Promise<CmdOutcome> {
    const id = `p${++this.nextId}`;
    this.send({ type: 'cmd', id, deviceId, action, params });
    return new Promise<CmdOutcome>((resolve, reject) => {
      const entry: Pending = {
        resolve: (r) => {
          clearTimeout(entry.timer);
          resolve({ result: r, chunks: entry.chunks });
        },
        reject,
        chunks: [],
        timer: 0 as unknown as ReturnType<typeof setTimeout>,
      };
      entry.timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`命令超时: ${action}`));
      }, timeoutMs);
      this.pending.set(id, entry);
    });
  }

  stop(): void {
    this.pending.clear();
  }

  /**
   * 发送即发即忘命令（不登记 pending、不等结果）。
   * 用于高频的鼠标移动等事件；返回的 cmd:result 因 id 未知会被 handle() 安全忽略。
   */
  cmdNoWait(deviceId: string, action: string, params: Record<string, unknown> = {}): void {
    try {
      this.send({ type: 'cmd', id: `n${++this.nextId}`, deviceId, action, params });
    } catch {
      /* 未连接时静默丢弃 */
    }
  }

  /* ------------------------------ 内部 ------------------------------ */

  private open(): void {
    this.onConnState?.('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.onConnState?.('closed');
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectDelay = 800;
      this.onConnState?.('open');
      this.nonce = makeNonce();
      ws.send(JSON.stringify({ type: 'auth', token: this.token, nonce: this.nonce }));
    };
    ws.onmessage = (ev) => this.handle(String(ev.data));
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* noop */
      }
    };
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null;
      this.onConnState?.('closed');
      this.rejectAll(new Error('连接已断开'));
      if (!this.manualClose) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.retryTimer || this.manualClose) return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 15_000);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.manualClose) this.open();
    }, delay);
  }

  private handle(raw: string): void {
    let m: ServerToPhoneMsg;
    try {
      m = JSON.parse(raw) as ServerToPhoneMsg;
    } catch {
      return;
    }
    if (!m || typeof m !== 'object') return;
    switch (m.type) {
      case 'auth:ok': {
        // 互认：校验服务器对本次 nonce 的 HMAC，防伪装服务器骗取 token
        const expect = makeProof(this.token, this.nonce);
        if (!m.proof || m.proof !== expect) {
          this.onAuthError?.('服务器身份校验失败（token 或地址不匹配）');
          this.disconnect();
          break;
        }
        this.onAuthOk?.(m.devices);
        break;
      }
      case 'auth:error':
        this.onAuthError?.(m.reason);
        break;
      case 'status':
        this.onStatus?.(m.devices);
        break;
      case 'cmd:result': {
        const p = this.pending.get(m.id);
        if (p) {
          this.pending.delete(m.id);
          p.resolve(m);
        }
        break;
      }
      case 'stream': {
        // 流式增量：协议仍保留（服务器透传），当前无业务产生；累积到 pending 备用
        const p = this.pending.get(m.id);
        if (p) p.chunks.push(m.chunk);
        break;
      }
      case 'screen.frame':
        this.onScreenFrame?.(m);
        break;
      case 'fs.event':
        this.onFsEvent?.(m);
        break;
      case 'probe.data':
        this.onProbeData?.(m);
        break;
      case 'error':
        this.onError?.(m.reason);
        break;
      default:
        break;
    }
  }

  private rejectAll(err: Error): void {
    for (const p of this.pending.values()) {
      clearTimeout(p.timer);
      p.reject(err);
    }
    this.pending.clear();
  }
}
